import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { getCreativeId } from 'client/engagement-handlers/helper';
import { getLeadType, getLeadUid } from 'site-modules/shared/components/lead-form/utils/utils';
import { LEAD_TYPES } from 'site-modules/shared/constants/lead-form/lead-form-params';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';

import './card.scss';

export function DealerListItem({
  leadTarget,
  showRating = true,
  showDistance = false,
  inventoryText = null,
  slot = 0,
}) {
  const cardRef = useRef();
  const { dealer, inventory, leadAttributes } = leadTarget;

  const dealerInfo = get(inventory, 'dealerInfo', dealer);
  const distance = get(leadAttributes, 'distance');

  const salesOverallRating = get(dealerInfo, 'displayInfo.salesOverallRating', get(dealerInfo, 'salesRating', 0));
  const salesReviewCount = get(dealerInfo, 'displayInfo.salesReviewCount', get(dealerInfo, 'salesReviewCount', 0));

  const uid = getLeadUid(leadTarget);
  const isInventoryTarget = getLeadType(leadTarget) === LEAD_TYPES.INVENTORY;

  useEffect(() => {
    const creativeId = getCreativeId(cardRef.current);

    if (showRating && !!salesReviewCount) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: 'rating_displayed',
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
          creative_id: creativeId,
          value: `${slot + 1},${salesOverallRating}`,
        },
      });
    }

    if (isInventoryTarget && !!inventoryText) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: 'vin_match_text_displayed',
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
          creative_id: creativeId,
          value: `${slot + 1}`,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dealer-list-item" ref={cardRef}>
      <div className="dealer-name medium text-gray-darker" id={uid}>
        {dealerInfo.name}
      </div>
      {showDistance && distance > 0 && <div className="xsmall">{distance.toFixed(1)} mi away</div>}
      {showRating && !!salesReviewCount && (
        <div className="small d-flex align-items-center">
          <RatingStars rating={salesOverallRating} className="text-primary-darker" />
          <span>({salesReviewCount})</span>
          <PopoverItem placement="top" screenReaderTooltipLabel="sales rating" trigger="click hover focus">
            Based on reviews posted in the prior 24 months.
          </PopoverItem>
        </div>
      )}
      {isInventoryTarget && !!inventoryText && (
        <ContentFragment classes="inventory-text small fst-italic text-gray-dark mt-0_25">
          {inventoryText}
        </ContentFragment>
      )}
    </div>
  );
}

DealerListItem.propTypes = {
  leadTarget: PropTypes.shape({
    inventory: PropTypes.shape({}),
    dealer: PropTypes.shape({}),
    leadAttributes: PropTypes.shape({
      primary: PropTypes.bool.isRequired,
      selected: PropTypes.bool.isRequired,
      distance: PropTypes.number,
    }).isRequired,
  }).isRequired,
  inventoryText: PropTypes.string,
  slot: PropTypes.number,
  showRating: PropTypes.bool,
  showDistance: PropTypes.bool,
};
